/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo } from "react";
import { When } from "react-if";
import styled from "styled-components";
import {
  Box,
  BrickLoader,
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Table,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  EventDetailResourceGroupFragment,
  EventStatus,
  IncomingEventStatus,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { ResourceItem } from "~/components/resourceGroup/ResourceItem";
import { DuplicateResourceState } from "~/hooks/useDuplicatedResources";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { getEventStatus } from "~/views/events/helpers";
import { useGetResourceGroup } from "~/views/resourceGroups/hooks";
import { NameWithLink } from "../Tables/cells/NameWithLink";
import { DuplicateResourceIcon } from "./DuplicateResourceIcon";

type Props = {
  resourceGroup: EventDetailResourceGroupFragment;
  status?: EventStatus | IncomingEventStatus;
  actions?: () => JSX.Element;
  duplicateState?: DuplicateResourceState;
};

export function ResourceGroupItem({
  resourceGroup,
  actions,
  status,
  duplicateState,
}: Props) {
  const { resourceGroup: group, loading } = useGetResourceGroup({
    resourceGroupId: resourceGroup.id,
  });

  const resources = useMemo(() => {
    const staticResources =
      group?.resourceSelectors?.flatMap((item) => item?.resource ?? []) ?? [];

    const dynamicResources =
      group?.resourceSelectors?.flatMap(
        (item) => item?.dynamicResources ?? []
      ) ?? [];

    return [...staticResources, ...dynamicResources];
  }, [group?.resourceSelectors]);

  useEffect(() => {
    if (!loading && group) {
      duplicateState?.onGroupLoad(group.id, resources);
    }
  }, [loading, group, resources, resourceGroup, duplicateState]);

  return (
    <LoaderWrap loading={loading} Component={BrickLoader}>
      <Box innerSpacing="spacing00">
        <StyledBox padding={theme.spacing.spacing03}>
          <FlexContainer>
            <When condition={isNotNil(status)}>
              <ExtendedTooltip
                caption={getEventStatus(status)?.description}
                position="start"
              >
                <SVGIcon
                  name={getEventStatus(status)?.icon}
                  css={{ marginLeft: "0", minWidth: theme.spacing.spacing04 }}
                  color={getEventStatus(status)?.color()}
                />
              </ExtendedTooltip>
            </When>
            <Text tag="div" weight="medium" ml={theme.spacing.spacing03}>
              <NameWithLink
                name={resourceGroup?.name}
                link={`${ROUTES.resourceGroups.generateDetails(
                  resourceGroup.id
                )}`}
              />
              <Text
                css={{ wordBreak: "break-word" }}
                tag="div"
                size="sm"
                color={theme.color.text.text02}
              >
                {resourceGroup.id}
              </Text>
            </Text>
            <div css={{ marginLeft: "auto" }}>{actions?.()}</div>
          </FlexContainer>
        </StyledBox>
        <Table>
          <Table.tbody>
            {resources?.map((resource, idx) => (
              <ResourceItem
                key={`${resourceGroup.id}-${idx}`}
                resource={resource}
                status={status}
                highlight={duplicateState?.isHighlighted(resource.id)}
                actions={
                  duplicateState?.duplicateResourceIds?.has(resource.id)
                    ? () => (
                        <DuplicateResourceIcon
                          resourceId={resource.id}
                          onIconClick={duplicateState?.onDuplicateIconClick}
                        />
                      )
                    : undefined
                }
              />
            ))}
          </Table.tbody>
        </Table>
      </Box>
    </LoaderWrap>
  );
}

const StyledBox = styled(Box)`
  border-bottom: 2px solid ${theme.color.border.border01};
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
`;
